import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import DonateDialog from "./DonateDialog";
import { MINISTRY } from "@/lib/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/blog", label: "Blog" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-plum/10 py-3" : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <span className="grid place-items-center w-11 h-11 rounded-full bg-plum text-gold font-heading text-2xl font-semibold shadow-md group-hover:scale-105 transition-transform">
            G
          </span>
          <span className="leading-tight">
            <span className="block font-heading text-lg font-semibold text-plum">Greater Faith</span>
            <span className="block text-[10px] tracking-[0.2em] uppercase text-stone">International Ministries</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium relative transition-colors hover:text-plum ${
                  isActive ? "text-plum" : "text-stone"
                } after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:bg-gold after:transition-all ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/get-involved"
            data-testid="nav-get-involved"
            className="text-sm font-medium text-plum border border-plum/20 rounded-full px-5 py-2.5 hover:bg-plum hover:text-cream transition-colors"
          >
            Get Involved
          </Link>
          <DonateDialog
            trigger={
              <button
                data-testid="nav-donate-btn"
                className="text-sm font-semibold bg-gold hover:bg-gold-dark text-plum rounded-full px-6 py-2.5 transition-colors flex items-center gap-2 shadow-sm hover:-translate-y-0.5 duration-300"
              >
                <Heart size={15} /> Donate
              </button>
            }
          />
        </div>

        <button
          className="lg:hidden text-plum p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-plum/10 mt-3" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="text-base font-medium text-plum"
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/get-involved" className="text-base font-medium text-plum">Get Involved</Link>
            <DonateDialog
              trigger={
                <button className="mt-2 w-full bg-gold text-plum font-semibold rounded-full py-3 flex items-center justify-center gap-2" data-testid="mobile-donate-btn">
                  <Heart size={16} /> Donate
                </button>
              }
            />
          </div>
        </div>
      )}
    </header>
  );
}
